import { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Grid, Typography } from "@mui/material";

import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";
import httpclient from "../../../Utils";

import { getColumns } from "./columns";
import { useBayLive } from "./useBinBayLive";
import StatusChangeConfirmationModal from "./StatusChangeConfirmationModal";
import Filter from "./Filter";
import AutoAssignPopup from "./AutoAssignPopup";
import FilterBtn from "../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../Assets/Icons/btn-filters-up.png";
import { createFilterHandler } from "../../../Utils/FilterUtils";

const INITIAL_FILTER_STATE = {
  bay: "",
  isLive: "",
};

const fieldMappings = {
  bay: ["bay", "$eq"],
  isLive: ["is_order_sync_live", "$eq"],
};

const valueTransformers = {
  // Convert boolean active to 1/0
  isLive: (value) => {
    if (value === "Yes") return 1;
    if (value === "No") return 0;
    return value; // Return original value if not boolean
  },
};

export default function BinBayLive() {
  const [selectedRow, setSelectedRow] = useState(null);
  const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
  const [changeStatus, setChangeStatus] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
const [openAutoAssignPopup, setOpenAutoAssignPopup] = useState(false);
const [selectedAutoAssignRow, setSelectedAutoAssignRow] = useState(null);
const [isAutoAssignChangingToNo, setIsAutoAssignChangingToNo] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const {
    binBayList,
    setBinBayList,
    loading,
    setLoading,
    rowCount,
    refresh,
    refreshBinBayList,
  } = useBayLive({
    autoFetch: true,
    pagination: {
      page: paginationModel.page, // Pass primitive values
      pageSize: paginationModel.pageSize,
    },
    sorting: sortModel,
  });
 const handleUpdateSuccess = () => {
    // Refresh the data in the table
    refreshBinBayList();
    
   
  };
  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setBinBayList,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `/bin-bay`,
    fieldMappings,
    valueTransformers,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };
const handleAutoAssignClick = (row) => {
  setSelectedAutoAssignRow(row);
  
  // If current value is 0 (No), changing to Yes - no priority needed
  // If current value is 1 (Yes), changing to No - need priority
  const isChangingToNo = row.is_auto_assigned === 1;
  
  setIsAutoAssignChangingToNo(isChangingToNo);
  setOpenAutoAssignPopup(true);
};
  const handleRemove = (field) => {
    // Create new filter data without the removed field
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    // Reset the field in filterData (UI state)
    newFilterData[field] = "";

    // Remove the field from submittedData (API state)
    delete newSubmittedData[field];

    // Update states
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    // Trigger API call with remaining filters
    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(INITIAL_FILTER_STATE);
  };

  const handleChangeStatus = async () => {
    setChangeStatus(true);
    const API_URL = `/bin-bay/status/${selectedRow.id}`;

    try {
      const { data } = await httpclient.post(API_URL);

      if (data.status === 200) {
        showNotification("success", data.message);
        setSelectedRow(null);
        setOpenStatusChangeModal(false);
        refreshBinBayList(); // Use the hook's refresh function
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      console.log(error, "error changing status");
      handleApiError(error);
    } finally {
      setChangeStatus(false);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = getColumns(setSelectedRow, setOpenStatusChangeModal,handleUpdateSuccess,handleAutoAssignClick);

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Live Bay
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            />
          </Grid>
          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />
        </Grid>

        <DataGrid
          rows={binBayList || []}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          loading={loading}
          slots={{
            loadingOverlay: CustomLoadingOverlay,
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              fontWeight: 500,
              color: "#48505E",
            },
          }}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
        />
        {openStatusChangeModal && (
          <StatusChangeConfirmationModal
            open={openStatusChangeModal}
            cancel={() => setOpenStatusChangeModal(false)}
            title="Go Live"
            item={selectedRow?.bay}
            loading={changeStatus}
            handleChangeStatus={handleChangeStatus}
          />
        )}
         {/* Auto Assign Popup */}
        <AutoAssignPopup
          open={openAutoAssignPopup}
          onClose={() => setOpenAutoAssignPopup(false)}
          rowData={selectedAutoAssignRow}
          onUpdateSuccess={() => {
            refreshBinBayList();
            setOpenAutoAssignPopup(false);
          }}
          isChangingToNo={isAutoAssignChangingToNo}
        />
      </Box>
      
    </Box>
  );
}
